import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage } from 'react-intl';

// Components

import ErrorMessage from '../../common/alerts/ErrorMessage';
import InstructionalText from '../notifications/InstructionalText';
import Form from '@lib/wrappers/Form';
import PageHeader from '../headers/PageHeader';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators / Selectors

import { changeData, clearDisabledUi } from '@actions/ssap/ssapActionCreators';

// Utils

import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { preventerTools } from '@utils/tools/preventerTools';
import { omit } from '@utils/miscellaneous/objectHelpers';

// Constants

import { BLOOD_RELATIONSHIP_TYPES } from '@constants/ssap/hhmTypes/bloodRelationshipCodesList';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';



/**
 *
 * @param {Object} props
 * @returns {React.Component} - Page 'Blood Relationship'
 *
 */
const Page2_13  = props => {


    const {
        allStatuses = [],
        disabledUi: {
            isVisible = false,
            allInputsDisabled,
            errorMessageReplacements,
        },
        data: {
            taxHousehold: {
                bloodRelationship = [],
                householdMember : householdMembers = [],
                householdMember: {
                    length : qtyOfHouseholdMembers
                }
            }
        },
        form,
        formName,
        relations: {
            dropdownOptions = [],
            loadedRelations = []
        },
    } = props;

    const {
        isFormSubmitted,
        isFormOnTopLevelValid
    } = preventerTools.provideObject(form[formName]);

    
    useEffect(() => {
        return () => {
            if(allInputsDisabled) {
                props.actions.clearDisabledUi();
            }
        };
    }, [ allInputsDisabled ]);

    const getDropdownOptions = () => {
        return dropdownOptions.map(o => ({
            text: `ssap.input.option.${o.type.toLowerCase()}`,
            value: o.code
        }));
    };   

    const spouseCode = (relation => relation ? relation.code : null)(loadedRelations.find(lr => lr.type === BLOOD_RELATIONSHIP_TYPES.SPOUSE));

    useEffect(() => {
        const {
            taxHousehold: {
                bloodRelationship: existingBloodRelations,
                bloodRelationship: {
                    length: qtyOfBloodRelations
                }
            }
        } = props.data;

        let updatedBloodRelations = [];
        let qtyOfDescribedHouseholdMembers = Math.sqrt(qtyOfBloodRelations);

        /**
         * Here we estimate if all household members are described in <bloodRelationship> Array (in the head of household) or not
         * If not we generate needed qty of describing relations objects and inject in this Array
         */

        if (qtyOfHouseholdMembers !== qtyOfDescribedHouseholdMembers) {
            updatedBloodRelations.push(...generateBloodRelations(existingBloodRelations));
            updateBloodRelationsAfterGenerating(updatedBloodRelations);
        }
    }, []);

    /**
     *
     * @param {Array} updatedBloodRelations - Array of objects after generation, each of them describes relation:
     * a) between two household members;
     * b) household member to himself
     *
     * If this function is invoked it updates <bloodRelationship> Array (in the head of household)
     */
    const updateBloodRelationsAfterGenerating = updatedBloodRelations => {
        let newState = { ...props.data };

        newState
            .taxHousehold
            .bloodRelationship = updatedBloodRelations;

        props.actions.changeData(newState);
    };

    /**
     *
     * @param {Array} existingBloodRelations - Array of objects in existing JSON, each of them describes relation:
     * a) between two household members;
     * b) household member to himself
     * @returns {Array} - Array of objects, each of them describes relation
     *
     * Because of logic that we need to describe relation of each household member to himself and to each
     * another household member we need Qty of household members powered by 2 objects
     *
     *  For example:
     *  1 household member - 1 object (to himself);
     *  2 household members - 4 objects (1st to 2nd, 1st to himself, 2nd to 1st, 2nd to himself)
     *  and so on...
     */
    const generateBloodRelations = existingBloodRelations => {
        let relations = [...existingBloodRelations];

        // here we iterate through all of household members
        
        householdMembers.forEach((member, index) => {

            const {
                applicantGuid: personGuid
            } = member;

            const describedRelation =
                existingBloodRelations
                    .find(br => [br.personGuid, br.relatedPersonGuid].every(guid => guid === personGuid));

            // If household member isn't described we add here relation of this household member to himself
            
            if (!describedRelation) {
                relations.push({
                    individualPersonId: String(index + 1),
                    relatedPersonId: String(index + 1),
                    relation: '18',
                    personGuid: personGuid,
                    relatedPersonGuid: personGuid
                });
            }

            // Here we iterate again to add relations of this household member to other household members
            
            householdMembers.forEach((m , i) => {
                
                const {
                    applicantGuid: relatedPersonGuid
                } = m;

                const describedRelation =
                    existingBloodRelations
                        .find(br => br.personGuid === personGuid && br.relatedPersonGuid === relatedPersonGuid);

                const getRelation = (personGuid, relatedPersonGuid) => {
                    const reflectedPerson = existingBloodRelations
                        .find(br => br.personGuid === personGuid && br.relatedPersonGuid === relatedPersonGuid);

                    if(!reflectedPerson) {
                        return null;
                    } else {
                        return (mirrorType => loadedRelations.find(rel => rel.type === mirrorType).code)(loadedRelations.find(rel => rel.code === reflectedPerson.relation).mirrorType);
                    }
                };

                if (!describedRelation) {
                    if (i !== index) {
                        relations.push({
                            individualPersonId: String(index + 1),
                            relatedPersonId: String(i + 1),
                            relation: getRelation(personGuid, relatedPersonGuid),
                            personGuid: personGuid,
                            relatedPersonGuid: relatedPersonGuid
                        });
                    }
                }
            });
        });

        relations.map(relation => ({
            ...relation,
            name: `relation_${relation.personGuid}_to_${relation.relatedPersonGuid}`,
        }));

        return relations;
    };

    /**
     *
     * @param {Array} householdMembers - Array of all household members
     * @returns {Array} - Array of Lis with names of household members
     */
    const getHouseholdMembersList = householdMembers => {

        return householdMembers.map((member, i) =>
            <li key={`${member.name.firstName}_${i}`}>
                {normalizeNameOfPerson(member.name)}
            </li>
        );
    };


    //returns an array of two items(of householdMembers) when there are two or more householdMembers

    const concatMessageReplacements = householdMembersArr => {
        return householdMembersArr.reduce((res, hhm, i, arr) => {
            const [ firstChunk ] = res;
            return i > 0 &&  i < arr.length - 1 ? 
                [
                    `${firstChunk}, ${hhm}`
                ] : 
                [
                    ...res,
                    hhm
                ];
        },[]);
    };

    /**
     *
     * @param {Array} args -    newData: updated relation (result of merging updated dropdown value and initial relation object)
     *                          value: actual relation code
     *
     * Replaces relation in <bloodRelationship> Array with updated relation
     */
    const onFieldChange = (...args) => {

        const { newData = {}, value } = parseFieldChangeArgs(args);

        /**
         *
         *  Here we rewrite to <relation> property the value from unique property in <bloodRelation> object
         */
        const updatedRelationPropData = {
            ...newData,
            relation: newData[`relation_${newData.personGuid}_to_${newData.relatedPersonGuid}`],
        };

        /**
         *
         * @param {string, null} relation - code of relation, that should be 'mirrored'
         * @returns {string, null} 'mirrored' relation - code of relation that matches initial value
         */
        const provideMirroredRelation = relation => {
            if(relation === null) {
                return null;
            } else {
                const mirrorType = loadedRelations.find(lr => lr.code === relation).mirrorType;
                return loadedRelations.find(lr => lr.type === mirrorType).code;
            }
        };

        const newDataReversed = {
            ...updatedRelationPropData,
            individualPersonId: updatedRelationPropData.relatedPersonId,
            relatedPersonId: updatedRelationPropData.individualPersonId,
            relation: provideMirroredRelation(updatedRelationPropData.relation),
            personGuid: updatedRelationPropData.relatedPersonGuid,
            relatedPersonGuid: updatedRelationPropData.personGuid
        };

        const newState = { ...props.data };

        // Cleaning up <spouse> selection if the same has been just chosen and related to previous selections

        if(value === spouseCode) {

            const result = [];

            const indexes = [];

            const bloodRelationships = newState
                .taxHousehold
                .bloodRelationship;
            
            bloodRelationships
                .forEach((br, i) => {
                    if(br.personGuid === newData.personGuid && br.relation === spouseCode) {
                        indexes.push(i);
                    }
                    if(br.relatedPersonGuid === newData.personGuid && br.relation === spouseCode) {
                        indexes.push(i);
                    }
                    if(br.relatedPersonGuid === newData.relatedPersonGuid && br.relation === spouseCode) {
                        indexes.push(i);
                    }
                    if(br.personGuid === newData.relatedPersonGuid && br.relation === spouseCode) {
                        indexes.push(i);
                    }
                });

            bloodRelationships
                .map((br, i) => {

                    if(indexes.some(idx => idx === i)) {
                        result.push({
                            ...br,
                            relation: null
                        });
                    } else {
                        result.push(br);
                    }

                });

            newState
                .taxHousehold
                .bloodRelationship = result;
        }

        newState
            .taxHousehold
            .bloodRelationship = newState
                .taxHousehold
                .bloodRelationship
                .map(bloodRelation =>
                    (bloodRelation.personGuid === updatedRelationPropData.relatedPersonGuid && // Here we found 'mirrored' relation
                    bloodRelation.relatedPersonGuid === updatedRelationPropData.personGuid) ?
                        newDataReversed :
                        (bloodRelation.personGuid === updatedRelationPropData.personGuid && // Here we found the same relation
                        bloodRelation.relatedPersonGuid === updatedRelationPropData.relatedPersonGuid) ?
                            updatedRelationPropData :
                            bloodRelation
                )
                .map(bloodRelation => omit(bloodRelation, // Here we clean up <bloodRelation> from unneeded properties
                    [
                        `relation_${bloodRelation.personGuid}_to_${bloodRelation.relatedPersonGuid}`,
                        `relation_${bloodRelation.relatedPersonGuid}_to_${bloodRelation.personGuid}`,
                    ]));

        newState
            .taxHousehold = {
                ...newState.taxHousehold,
                primaryTaxFilerPersonId: 0,
                householdMember: newState
                    .taxHousehold
                    .householdMember.map(hhm => {
                        return {
                            ...hhm,
                            householdId: 0,
                            taxHouseholdComposition: {
                                ...hhm.taxHouseholdComposition,
                                ...EMPTY_OBJECTS.HOUSEHOLD_COMPOSITION
                            }
                        };
                    })
            };

        props.actions.changeData(newState);
    };

    const checkTopLevelValidity = () => {
        return allStatuses.every(status => {
            const { hhmStatus: { currentHhm: { isYoungerThanParents, isOlderThanChildren } } } = status;
            return !isYoungerThanParents && !isOlderThanChildren;
        });
    };

    const getAgeValidationErrorMessage = (member, currentMemberPersonId) => {
        const isErrorMessageShown = (
            status => {
                if(status) {
                    const { hhmStatus: { currentHhm: { isYoungerThanParents, isOlderThanChildren } } } = status;
                    switch(true) {
                        case !isYoungerThanParents && !isOlderThanChildren:
                            return false;
                        case !!isYoungerThanParents:
                            return isYoungerThanParents.some(ids => ids > currentMemberPersonId);
                        case !!isOlderThanChildren:
                            return isOlderThanChildren.some(ids => ids > currentMemberPersonId);
                        default:
                            return false;
                    }
                } else {
                    console.error('Status not found by GUID');
                    return false;
                }
            }
        )(allStatuses.find(status => status.hhmGuid === member.applicantGuid));

        const errorMessage = (
            <ValidationErrorMessage
                isVisible={isFormSubmitted && !isFormOnTopLevelValid}
                messageText={VALIDATION_ERROR_MESSAGES.CHILD_PARENT_AGE_MATCH}
                errorClasses={['margin-l-0', 'margin-b-10']}
            />
        );

        return isErrorMessageShown ? errorMessage : null;
    };

    return (
        <Fragment>
            <div id="fhRelationship" className="fade-in">
                <div className="subsection">
                    <PageHeader text='ssap.subheader.household_relationship_details'/>
                    <div className="grid-row">
                        <div className="usa-alert gi-alert gi-alert__left-accent gi-alert__info">
                            <div className="gi-alert__body">
                                <p className="gi-alert__text">
                                    <FormattedMessage id="ssap.start.household_relations.notification" values={{ qtyOfHouseholdMembers }}/>
                                </p>
                                <ol className="usa-list gi-list--ol">
                                    {
                                        getHouseholdMembersList(householdMembers)
                                    }
                                </ol>
                            </div>
                        </div>
                    </div>

                    {   isVisible &&
                        <ErrorMessage
                            { ...{
                                errorMessage: 'ssap.validation_error_message.blood_relationship',
                                errorMessageReplacements: concatMessageReplacements(errorMessageReplacements),
                                parentAction: {
                                    closeError: props.actions.clearDisabledUi
                                }
                            } }
                        />
                    }
                    

                    <Form { ...{ formName, topLevelValidity: checkTopLevelValidity() } }>
                        {
                            householdMembers.map((member, i) => {
                                if(i !== qtyOfHouseholdMembers - 1) {
                                    return (
                                        <fieldset key={`${member.name.firstName}_${i}`} className="usa-fieldset">
                                            { getAgeValidationErrorMessage(member, i + 1) }
                                            <legend>
                                                <div className="grid-col-12 margin-t-20" data-testid = "how_is_related_legend" >
                                                    <InstructionalText
                                                        Component = {
                                                            <p data-instructions="instructional_text.how_is_related" className="margin-0" id={`how_is_related_legend_text_${i}`}>
                                                                {'ssap.start.household_relations.how_is_related'}
                                                            </p>
                                                        }
                                                        replacements={{
                                                            br: <br/>
                                                        }}
                                                        childrenReplacements = {{
                                                            serialNumber: i + 1,
                                                            personName: (
                                                                <strong>
                                                                    {normalizeNameOfPerson(member.name)}
                                                                </strong>
                                                            )
                                                        }}
                                                    />
                                                </div>
                                            </legend>

                                            <div role="group" aria-labelledby={`how_is_related_legend_text_${i}`}>
                                                {
                                                    bloodRelationship.map(bloodRelation => {

                                                        const {
                                                            individualPersonId,
                                                            relatedPersonId,
                                                            relation,
                                                            personGuid,
                                                            relatedPersonGuid
                                                        } = bloodRelation;

                                                        if(+individualPersonId === i + 1 && +relatedPersonId > i + 1) {

                                                            return (
                                                                <UniversalInput
                                                                    key = { `${ individualPersonId }_${ relatedPersonId }` }
                                                                    label = { {
                                                                        labelClass: ['text-break'],
                                                                        leftLabel: {
                                                                            text: 'ssap.input.label.relation_between_persons_left_side',
                                                                            replacements: {
                                                                                personName: normalizeNameOfPerson(member.name)
                                                                            }
                                                                        },
                                                                        rightLabel: {
                                                                            text: 'ssap.input.label.relation_between_persons_right_side',
                                                                            replacements: {
                                                                                relatedPersonName: householdMemberTools.extractFromHouseholdMembers(householdMembers).getFullNameByHhmId(+relatedPersonId)
                                                                            }
                                                                        }
                                                                    } }
                                                                    inputData = { {
                                                                        disabled: allInputsDisabled,
                                                                        isInputRequired: true,
                                                                        type: INPUT_TYPES.DROPDOWN_WITH_TWO_LABELS,
                                                                        name: `relation_${ personGuid }_to_${ relatedPersonGuid }`,
                                                                        value: relation,
                                                                        options: getDropdownOptions(),
                                                                        validationType: VALIDATION_TYPES.CHOSEN,
                                                                        currentData: bloodRelation,
                                                                        fields: []
                                                                    }}
                                                                    inputActions = { {
                                                                        onFieldChange
                                                                    } }
                                                                />
                                                            );
                                                        }
                                                    })
                                                }
                                            </div>
                                        </fieldset>
                                    );
                                }
                            })
                        }
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    data: state.ssap.data,
    disabledUi: state.ssap.disabledUi,
    form: state.ssap.form,
    relations: state.ssap.relations
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        clearDisabledUi
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page2_13);
